import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import BodyContainer from "../components/BodyContainer";
import Title from "../components/Title";
import ProgressRing from "../components/ProgressRing";
import {useParams} from "react-router-dom";
import {getSchedule, isScheduleItemFinished} from "../handlers/schedulehandler";
import LiveScheduleCard from "../components/LiveScheduleCard";
import {Badge, Button} from "@fluentui/react-components";
import {ArrowClockwise16Regular} from "@fluentui/react-icons";

const SYNC_INTERVAL_SECONDS = 15;

const getScheduleState = (scheduleItem) => {
    if (!scheduleItem) {
        return `empty`;
    }

    if (isScheduleItemFinished(scheduleItem)) {
        return `finished`;
    }

    if (scheduleItem.race_start_time) {
        return `started`;
    }

    return `upcoming`;
};

const getScheduleKey = (scheduleItem) => {
    if (!scheduleItem) {
        return `none`;
    }

    return scheduleItem.id ?? `${scheduleItem.race_nr}-${scheduleItem.planned_start_time}`;
};

const isNewStartedRace = (currentItem, previousItem) => {
    if (!currentItem || getScheduleState(currentItem) !== `started`) {
        return false;
    }

    return !previousItem || !previousItem.race_start_time;
};

const isNewFinishedRace = (currentItem, previousItem) => {
    if (!currentItem || getScheduleState(currentItem) !== `finished`) {
        return false;
    }

    const currentResultsLength = Array.isArray(currentItem.results) ? currentItem.results.length : 0;
    const previousResultsLength = Array.isArray(previousItem?.results) ? previousItem.results.length : 0;

    return !previousItem || !isScheduleItemFinished(previousItem) || currentResultsLength > previousResultsLength;
};

const sortByPlannedStart = (a, b) => new Date(a.planned_start_time) - new Date(b.planned_start_time);

const sortByLatestPlannedStart = (a, b) => new Date(b.planned_start_time) - new Date(a.planned_start_time);

const pickRaceForBroadcast = (scheduleItems, previousSnapshot) => {
    const items = Array.isArray(scheduleItems) ? scheduleItems : [];

    if (items.length === 0) {
        return {item: null, state: `empty`};
    }

    const liveItems = items.filter((item) => item.race_start_time && !isScheduleItemFinished(item)).sort(sortByPlannedStart);
    const upcomingItems = items.filter((item) => !item.race_start_time && !isScheduleItemFinished(item)).sort(sortByPlannedStart);
    const finishedItems = items.filter((item) => isScheduleItemFinished(item)).sort(sortByLatestPlannedStart);

    if (!previousSnapshot || previousSnapshot.size === 0) {
        if (upcomingItems.length > 0) {
            return {item: upcomingItems[0], state: `upcoming`};
        }

        if (liveItems.length > 0) {
            return {item: liveItems[0], state: `started`};
        }

        return {item: finishedItems[0] ?? null, state: finishedItems[0] ? `finished` : `empty`};
    }

    const startedRace = liveItems.find((currentItem) => {
        const previousItem = previousSnapshot.get(getScheduleKey(currentItem));
        return isNewStartedRace(currentItem, previousItem);
    });

    if (startedRace) {
        return {item: startedRace, state: `started`};
    }

    const finishedRace = finishedItems.find((currentItem) => {
        const previousItem = previousSnapshot.get(getScheduleKey(currentItem));
        return isNewFinishedRace(currentItem, previousItem);
    });

    if (finishedRace) {
        return {item: finishedRace, state: `finished`};
    }

    if (upcomingItems.length > 0) {
        return {item: upcomingItems[0], state: `upcoming`};
    }

    if (liveItems.length > 0) {
        return {item: liveItems[0], state: `started`};
    }

    return {item: finishedItems[0] ?? null, state: finishedItems[0] ? `finished` : `empty`};
};

const LiveScheduleView = () => {
    const {id} = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [schedule, setSchedule] = useState(null);
    const [isSyncing, setIsSyncing] = useState(false);
    const [lastSynced, setLastSynced] = useState(null);
    const [syncBarKey, setSyncBarKey] = useState(0);
    const [featuredRace, setFeaturedRace] = useState(null);
    const [featuredRaceState, setFeaturedRaceState] = useState(`empty`);
    const previousSnapshotRef = useRef(new Map());

    const updateBroadcastView = useCallback((result) => {
        const snapshot = new Map(Array.isArray(result) ? result.map((item) => [getScheduleKey(item), item]) : []);
        const selection = pickRaceForBroadcast(result, previousSnapshotRef.current);

        setSchedule(result);
        setFeaturedRace(selection.item);
        setFeaturedRaceState(selection.state);
        previousSnapshotRef.current = snapshot;
        setLastSynced(new Date());
        setSyncBarKey((prev) => prev + 1);
    }, []);

    const fetchSchedule = useCallback(async (showFullLoading = false) => {
        if (showFullLoading) setIsLoading(true);
        setIsSyncing(true);

        const result = await getSchedule(id);
        updateBroadcastView(result);

        setIsSyncing(false);
        if (showFullLoading) setIsLoading(false);
    }, [id, updateBroadcastView]);

    useEffect(() => {
        fetchSchedule(true).then(() => {/*ignored*/});

        const interval = setInterval(() => {
            fetchSchedule(false).then(() => {/*ignored*/});
        }, SYNC_INTERVAL_SECONDS * 1000);

        return () => clearInterval(interval);
    }, [fetchSchedule]);

    const featuredLabel = useMemo(() => {
        if (featuredRaceState === `started`) return `Race gestart`;
        if (featuredRaceState === `finished`) return `Nieuwe uitslag`;
        if (featuredRaceState === `upcoming`) return `Volgende race`;
        return ``;
    }, [featuredRaceState]);

    const scheduleIsEmpty = !isLoading && schedule != null && schedule.length === 0;

    return (
        <BodyContainer showBackButton={true} content={
            <>
                <div className={`flex flex-align-center flex-gap-medium flex-wrap`}>
                    <Title title={`Live Wedstrijdschema`}/>
                </div>

                {isLoading && <ProgressRing/>}

                {!isLoading && schedule == null && (
                    <p>Er is geen wedstrijdschema beschikbaar.</p>
                )}

                {!isLoading && scheduleIsEmpty && (
                    <p>Er zijn nog geen races om weer te geven.</p>
                )}

                {!isLoading && schedule != null && featuredRace != null && (
                    <>
                        <div className={`live-sync-bar-container`} key={syncBarKey}>
                            <div className={`live-sync-bar`} style={{animationDuration: `${SYNC_INTERVAL_SECONDS}s`}}/>
                        </div>

                        <div className={`flex flex-align-center flex-gap-small live-sync-status flex-wrap`}>
                            <Badge appearance="filled" color={featuredRaceState === `finished` ? `success` : featuredRaceState === `started` ? `warning` : `brand`}>
                                {featuredLabel}
                            </Badge>
                            <ArrowClockwise16Regular className={isSyncing ? `live-sync-icon--spinning` : ``}/>
                            {isSyncing
                                ? <span>Synchroniseren&hellip;</span>
                                : <span>Automatisch gesynchroniseerd elke {SYNC_INTERVAL_SECONDS}s</span>
                            }
                            {lastSynced && !isSyncing && (
                                <span className={`live-sync-time`}>
                                    &middot; {lastSynced.toLocaleTimeString("nl-NL", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    second: "2-digit"
                                })}
                                </span>
                            )}
                        </div>

                        <div className={`live-section`}>
                            <LiveScheduleCard
                                key={`${featuredRace.id}-${featuredRaceState}`}
                                scheduleItem={featuredRace}
                                animationDelay={0}
                                broadcast={true}
                            />
                        </div>
                    </>
                )}
            </>
        }/>
    );
};

export default LiveScheduleView;
